<template>
  <div class="all-franchises-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            {{ displayCategoryName ? `${displayCategoryName} Franchises` : 'Discover Franchise Opportunities' }}
          </h1>
          <p class="hero-subtitle">
            {{ displayCategoryName 
              ? `Explore ${filteredFranchises.length} verified ${displayCategoryName.toLowerCase()} franchise opportunities across India`
              : 'Explore hundreds of verified franchise opportunities across India'
            }}
          </p>
          <div v-if="displayCategoryName" class="category-breadcrumb">
            <router-link to="/franchises" class="breadcrumb-link">
              <i class="fa fa-arrow-left"></i>
              Back to All Categories
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Search and Filter Section -->
    <section class="filter-section">
      <div class="container">
        <div class="filter-container">
          <!-- Search Bar -->
          <div class="search-container">
            <div class="search-input-wrapper">
              <i class="fa fa-search search-icon"></i>
              <input 
                v-model="searchQuery" 
                type="text" 
                placeholder="Search franchises by name, category, or location..."
                class="search-input"
                @input="handleSearch"
              />
            </div>
          </div>

          <!-- Filter Options -->
          <div class="filter-options">
            <div class="filter-group">
              <label class="filter-label">Category</label>
              <select v-model="selectedCategory" @change="handleCategoryFilter" class="filter-select">
                <option value="">All Categories</option>
                <option v-for="category in dynamicCategories" :key="category.value" :value="category.value">
                  {{ category.label }} ({{ category.count }})
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Investment Range</label>
              <select v-model="selectedInvestment" @change="handleInvestmentFilter" class="filter-select">
                <option value="">All Investment Ranges</option>
                <option v-for="range in investmentRanges" :key="range.value" :value="range.value">
                  {{ range.label }} ({{ range.count }})
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Location</label>
              <select v-model="selectedLocation" @change="handleLocationFilter" class="filter-select">
                <option value="">All Locations</option>
                <option v-for="location in dynamicLocations" :key="location.value" :value="location.value">
                  {{ location.label }} ({{ location.count }})
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Sort By</label>
              <select v-model="sortBy" @change="handleSort" class="filter-select">
                <option value="name">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="investment">Investment Low-High</option>
                <option value="investment-desc">Investment High-Low</option>
                <option value="roi">ROI Low-High</option>
                <option value="roi-desc">ROI High-Low</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          <!-- Clear Filters -->
          <div class="clear-filters">
            <button @click="clearFilters" class="clear-btn">
              <i class="fa fa-times"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </section>

        <!-- Results Section -->
    <section class="bg-sectionwize covidproof section-30 generic">
      <div class="container">
        <!-- Results Header -->
        <div class="row justify-content-center">
          <div class="col-md-12">
            <div class="section-ptb text-center">
              <h2 class="trending-title mb-4">
                {{ filteredFranchises.length }} Franchise{{ filteredFranchises.length !== 1 ? 's' : '' }} Found
              </h2>
              <p class="results-subtitle">
                Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalFranchises }} results
              </p>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="franchiseStore.loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="franchiseStore.error" class="text-center text-danger">
          {{ franchiseStore.error }}
        </div>

        <!-- No Results State -->
        <div v-else-if="filteredFranchises.length === 0" class="text-center">
          <p>No franchises found.</p>
        </div>

        <!-- Franchises Grid -->
        <div v-else class="row justify-content-center">
          <div class="col-md-12">
            <div class="franchises-grid">
              <CardSection 
                v-for="franchise in paginatedFranchises" 
                :key="franchise.id" 
                :franchise="franchise" 
                class="franchise-card-item" 
              />
            </div>
            
            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination-container">
              <div class="pagination">
                <button 
                  @click="currentPage = 1" 
                  :disabled="currentPage === 1"
                  class="pagination-btn"
                >
                  <i class="fa fa-angle-double-left"></i>
                </button>
                
                <button 
                  @click="currentPage--" 
                  :disabled="currentPage === 1"
                  class="pagination-btn"
                >
                  <i class="fa fa-angle-left"></i>
                </button>
                
                <div class="page-numbers">
                  <button 
                    v-for="page in visiblePages" 
                    :key="page"
                    @click="currentPage = page"
                    :class="['page-btn', { active: currentPage === page }]"
                  >
                    {{ page }}
                  </button>
                </div>
                
                <button 
                  @click="currentPage++" 
                  :disabled="currentPage === totalPages"
                  class="pagination-btn"
                >
                  <i class="fa fa-angle-right"></i>
                </button>
                
                <button 
                  @click="currentPage = totalPages" 
                  :disabled="currentPage === totalPages"
                  class="pagination-btn"
                >
                  <i class="fa fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFranchiseStore } from '@/stores/franchiseStore'
import { mapFranchiseData } from '@/utils/formatters'
import CardSection from '../components/CardSection.vue'

const franchiseStore = useFranchiseStore()
const route = useRoute()
const router = useRouter()

// Reactive data
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedInvestment = ref('')
const selectedLocation = ref('')
const sortBy = ref('name')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)

// Load franchises
const loadFranchises = async () => {
  await franchiseStore.fetchFranchises()
}

onMounted(async () => {
  await loadFranchises()
  
  // Set filters from URL parameters
  if (route.params.slug) {
    selectedCategory.value = route.params.slug
  }
  
  // Set filters from query parameters
  if (route.query.category) {
    selectedCategory.value = route.query.category
  }
  if (route.query.investment) {
    selectedInvestment.value = route.query.investment
  }
  if (route.query.location) {
    selectedLocation.value = route.query.location
  }
  if (route.query.sort) {
    sortBy.value = route.query.sort
  }
  if (route.query.search) {
    searchQuery.value = route.query.search
  }
})

// Computed properties
const allFranchises = computed(() => {
  if (!franchiseStore.franchises || !Array.isArray(franchiseStore.franchises)) {
    return []
  }
  return franchiseStore.franchises.map(franchise => mapFranchiseData(franchise))
})

// Dynamic filter options with counts
const dynamicCategories = computed(() => {
  const categoryCounts = {}
  
  allFranchises.value.forEach(franchise => {
    if (franchise.category) {
      categoryCounts[franchise.category] = (categoryCounts[franchise.category] || 0) + 1
    }
  })
  
  return Object.entries(categoryCounts)
    .map(([category, count]) => ({
      label: category,
      value: categoryNameToSlug(category),
      count
    }))
    .sort((a, b) => b.count - a.count) // Sort by count descending
})

const investmentRanges = computed(() => {
  const rangeCounts = {
    '0-5': 0,
    '5-10': 0,
    '10-25': 0,
    '25-50': 0,
    '50+': 0
  }
  
  allFranchises.value.forEach(franchise => {
    const investment = franchise.investment
    if (!investment) return
    
    let min, max
    if (typeof investment === 'string' && investment.includes('-')) {
      [min, max] = investment.split('-').map(v => parseFloat(v.replace(/[^\d.]/g, '')))
    } else {
      min = parseFloat(investment) || 0
      max = parseFloat(franchise.investmentMax) || min
    }
    
    // Normalize to Lakhs for comparison if values are large (assuming raw values are in absolute INR)
    // The filter values like '5-10' seem to imply Lakhs (5L - 10L). 
    // If parsed values are > 1000, they are probably absolute. 5 Lakhs = 500000.
    // Let's normalize absolute values to Lakhs for categorization.
    let minLakhs = min
    let maxLakhs = max
    
    if (min > 1000) minLakhs = min / 100000
    if (max > 1000) maxLakhs = max / 100000

    if (maxLakhs <= 5) rangeCounts['0-5']++
    else if (minLakhs >= 5 && maxLakhs <= 10) rangeCounts['5-10']++
    else if (minLakhs >= 10 && maxLakhs <= 25) rangeCounts['10-25']++
    else if (minLakhs >= 25 && maxLakhs <= 50) rangeCounts['25-50']++
    else if (minLakhs >= 50) rangeCounts['50+']++
  })
  
  return [
    { label: 'Under ₹5L', value: '0-5', count: rangeCounts['0-5'] },
    { label: '₹5L - ₹10L', value: '5-10', count: rangeCounts['5-10'] },
    { label: '₹10L - ₹25L', value: '10-25', count: rangeCounts['10-25'] },
    { label: '₹25L - ₹50L', value: '25-50', count: rangeCounts['25-50'] },
    { label: 'Above ₹50L', value: '50+', count: rangeCounts['50+'] }
  ].filter(range => range.count > 0)
})

const dynamicLocations = computed(() => {
  const locationCounts = {}
  
  allFranchises.value.forEach(franchise => {
    const location = franchise.location || franchise.headquarters || franchise.territory
    if (location) {
      if (Array.isArray(location)) {
        location.forEach(loc => {
          locationCounts[loc] = (locationCounts[loc] || 0) + 1
        })
      } else {
        locationCounts[location] = (locationCounts[location] || 0) + 1
      }
    }
  })
  
  return Object.entries(locationCounts)
    .map(([location, count]) => ({
      label: location,
      value: location,
      count
    }))
    .sort((a, b) => b.count - a.count) // Sort by count descending
})

const categories = computed(() => {
  const cats = [...new Set(allFranchises.value.map(f => f.category).filter(Boolean))]
  return cats.sort()
})

// Function to convert slug to category name
const slugToCategoryName = (slug) => {
  const categoryMap = {
    'food-beverage': 'Food & Beverage',
    'food-and-beverage': 'Food & Beverage',
    'food-and-beverages': 'Food & Beverage',
    'health-wellness': 'Health & Wellness',
    'health-and-wellness': 'Health & Wellness',
    'retail': 'Retail',
    'education': 'Education',
    'healthcare': 'Healthcare',
    'automobile': 'Automobile',
    'automotive': 'Automobile',
    'technology': 'Technology',
    'services': 'Services',
    'fitness': 'Fitness',
    'beauty': 'Beauty',
    'real-estate': 'Real Estate',
    'finance': 'Finance',
    'travel': 'Travel',
    'entertainment': 'Entertainment',
    'consulting': 'Consulting',
    'manufacturing': 'Manufacturing',
    'logistics': 'Logistics',
    'franchise': 'Franchise',
    'business-opportunities': 'Business Opportunities'
  }
  
  // First try exact match
  if (categoryMap[slug]) {
    return categoryMap[slug]
  }
  
  // Then try to find a category that contains the slug
  const availableCategories = categories.value
  
  const matchingCategory = availableCategories.find(cat => {
    const catSlug = cat.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/&/g, '')
    const matches = catSlug === slug || cat.toLowerCase().replace(/\s+/g, '').replace(/&/g, '') === slug.replace(/-/g, '')
    return matches
  })
  
  if (matchingCategory) {
    return matchingCategory
  }
  
  return slug
}

// Function to convert category name to slug
const categoryNameToSlug = (categoryName) => {
  if (!categoryName) return ''
  return categoryName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/&/g, '')
}

const filteredFranchises = computed(() => {
  let filtered = allFranchises.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(franchise => 
      franchise.name?.toLowerCase().includes(query) ||
      franchise.category?.toLowerCase().includes(query) ||
      franchise.location?.toLowerCase().includes(query) ||
      franchise.headquarters?.toLowerCase().includes(query)
    )
  }

  // Category filter
  if (selectedCategory.value) {
    const categoryName = slugToCategoryName(selectedCategory.value)
    
    filtered = filtered.filter(franchise => {
      const matches = franchise.category === categoryName
      return matches
    })
  }

  // Investment filter
  if (selectedInvestment.value) {
    filtered = filtered.filter(franchise => {
      const investment = franchise.investment
      if (!investment) return false
      
      let min, max
      if (typeof investment === 'string' && investment.includes('-')) {
        [min, max] = investment.split('-').map(v => parseFloat(v.replace(/[^\d.]/g, '')))
      } else {
        min = parseFloat(investment) || 0
        max = parseFloat(franchise.investmentMax) || min
      }

      // Normalize to Lakhs
      let minLakhs = min
      let maxLakhs = max
      if (min > 1000) minLakhs = min / 100000
      if (max > 1000) maxLakhs = max / 100000
      
      switch (selectedInvestment.value) {
        case '0-5': return maxLakhs <= 5
        case '5-10': return minLakhs >= 5 && maxLakhs <= 10
        case '10-25': return minLakhs >= 10 && maxLakhs <= 25
        case '25-50': return minLakhs >= 25 && maxLakhs <= 50
        case '50+': return minLakhs >= 50
        default: return true
      }
    })
  }

  // Location filter
  if (selectedLocation.value) {
    filtered = filtered.filter(franchise => {
      const location = franchise.location || franchise.headquarters || franchise.territory
      if (!location) return false
      
      if (Array.isArray(location)) {
        return location.some(loc => loc.toLowerCase().includes(selectedLocation.value.toLowerCase()))
      } else {
        return location.toLowerCase().includes(selectedLocation.value.toLowerCase())
      }
    })
  }

  // Sort
  filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'name':
        return a.name?.localeCompare(b.name) || 0
      case 'name-desc':
        return b.name?.localeCompare(a.name) || 0
      case 'investment':
        return (a.investment || '').localeCompare(b.investment || '')
      case 'investment-desc':
        return (b.investment || '').localeCompare(a.investment || '')
      case 'roi':
        return (a.expectedRoi || 0) - (b.expectedRoi || 0)
      case 'roi-desc':
        return (b.expectedRoi || 0) - (a.expectedRoi || 0)
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
      default:
        return 0
    }
  })

  return filtered
})

// Computed property for display category name
const displayCategoryName = computed(() => {
  if (!selectedCategory.value) return ''
  return slugToCategoryName(selectedCategory.value)
})

const totalFranchises = computed(() => filteredFranchises.value.length)
const totalPages = computed(() => Math.ceil(totalFranchises.value / itemsPerPage.value))
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value)
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage.value, totalFranchises.value))
const paginatedFranchises = computed(() => 
  filteredFranchises.value.slice(startIndex.value, endIndex.value)
)

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// Methods
const handleSearch = () => {
  currentPage.value = 1
  updateURL()
}

const handleCategoryFilter = () => {
  currentPage.value = 1
  updateURL()
}

const handleInvestmentFilter = () => {
  currentPage.value = 1
  updateURL()
}

const handleLocationFilter = () => {
  currentPage.value = 1
  updateURL()
}

const handleSort = () => {
  currentPage.value = 1
  updateURL()
}

const updateURL = () => {
  const params = new URLSearchParams()
  
  if (selectedCategory.value) params.set('category', selectedCategory.value)
  if (selectedInvestment.value) params.set('investment', selectedInvestment.value)
  if (selectedLocation.value) params.set('location', selectedLocation.value)
  if (sortBy.value !== 'name') params.set('sort', sortBy.value)
  if (searchQuery.value) params.set('search', searchQuery.value)
  
  const queryString = params.toString()
  const newPath = queryString ? `/franchises?${queryString}` : '/franchises'
  
  router.push(newPath)
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedCategory.value = ''
  selectedInvestment.value = ''
  selectedLocation.value = ''
  sortBy.value = 'name'
  currentPage.value = 1
  router.push('/franchises')
}

// Watch for route changes
watch(() => route.params.slug, (newSlug) => {
  if (newSlug) {
    selectedCategory.value = newSlug
  } else {
    selectedCategory.value = ''
  }
  currentPage.value = 1
})

// Watch for query parameter changes
watch(() => route.query, (newQuery) => {
  if (newQuery.category !== undefined) {
    selectedCategory.value = newQuery.category
  }
  if (newQuery.investment !== undefined) {
    selectedInvestment.value = newQuery.investment
  }
  if (newQuery.location !== undefined) {
    selectedLocation.value = newQuery.location
  }
  if (newQuery.sort !== undefined) {
    sortBy.value = newQuery.sort
  }
  if (newQuery.search !== undefined) {
    searchQuery.value = newQuery.search
  }
  currentPage.value = 1
}, { deep: true })

// Watch for changes
watch([searchQuery, selectedCategory, selectedInvestment, selectedLocation, sortBy], () => {
  currentPage.value = 1
})
</script>


<style scoped>
.all-franchises-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  padding: 80px 0 60px 0;
  color: white;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.08);
  color: #fff;
}

.hero-subtitle {
  font-size: 1.3rem;
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.category-breadcrumb {
  margin-top: 20px;
}

.breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: opacity 0.3s ease;
}

.breadcrumb-link:hover {
  opacity: 0.8;
}

/* Filter Section */
.filter-section {
  background: white;
  padding: 40px 0;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.filter-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.search-container {
  width: 100%;
}

.search-input-wrapper {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 1.1rem;
}

.search-input {
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.search-input:focus {
  outline: none;
  border-color: #ff6b35;
  background: white;
  box-shadow: 0 0 0 3px rgba(255,107,53,0.1);
}

.filter-options {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.filter-options .filter-group {
  min-width: 200px;
  flex: 1;
  max-width: 250px;
}

@media (max-width: 768px) {
  .filter-options {
    flex-direction: column;
    align-items: center;
  }
  
  .filter-options .filter-group {
    width: 100%;
    max-width: 300px;
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-select {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  min-width: 180px;
  transition: all 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: #ff6b35;
  box-shadow: 0 0 0 3px rgba(255,107,53,0.1);
}

.clear-filters {
  text-align: center;
}

.clear-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: 2px solid #ff6b35;
  color: #ff6b35;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.clear-btn:hover {
  background: #ff6b35;
  color: white;
}

/* Results Section */
.results-section {
  padding: 60px 0;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding: 0 20px;
}

.results-title {
  font-size: 2rem;
  font-weight: 700;
  color: #181818;
  margin: 0;
}

.results-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 8px 0 0 0;
}

.view-toggle {
  display: flex;
  gap: 8px;
}

.toggle-btn {
  padding: 12px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}

.toggle-btn.active {
  border-color: #ff6b35;
  color: #ff6b35;
  background: rgba(255,107,53,0.05);
}

.toggle-btn:hover {
  border-color: #ff6b35;
  color: #ff6b35;
}

/* Franchises Grid */
.franchises-container {
  margin-bottom: 60px;
}

.franchises-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 40px;
}

@media (max-width: 1200px) {
  .franchises-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .franchises-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .franchises-grid {
    grid-template-columns: 1fr;
  }
}

.franchise-card-item {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
}

.franchise-card {
  transition: transform 0.3s ease;
}

.franchise-card:hover {
  transform: translateY(-4px);
}

/* Loading State */
.loading-container {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #ff6b35;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 500;
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 20px;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  color: #f59e0b;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.error-message {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.retry-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: #e55a2b;
  transform: translateY(-1px);
}

/* No Results State */
.no-results-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 80px 20px;
  text-align: center;
}

.no-results-icon {
  font-size: 3rem;
  color: #d1d5db;
}

.no-results-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.no-results-message {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 400px;
}

.clear-filters-btn {
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.clear-filters-btn:hover {
  background: #e55a2b;
  transform: translateY(-1px);
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  border-color: #ff6b35;
  color: #ff6b35;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
  min-width: 44px;
}

.page-btn.active {
  background: #ff6b35;
  border-color: #ff6b35;
  color: white;
}

.page-btn:hover:not(.active) {
  border-color: #ff6b35;
  color: #ff6b35;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.1rem;
  }
  
  .filter-options {
    flex-direction: column;
    align-items: center;
  }
  
  .filter-select {
    min-width: 200px;
  }
  
  .results-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .franchises-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }
  
  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .filter-section {
    padding: 24px 0;
  }
  
  .results-section {
    padding: 40px 0;
  }
  
  .franchises-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .pagination {
    gap: 4px;
  }
  
  .page-btn {
    padding: 10px 12px;
    min-width: 40px;
  }
}
</style>